import * as React from 'react';
import {
  Box,
  queryHelpers,
  useMediaQuery,
  focusVisibleOverrideInner,
} from '@leagueplatform/genesis-core';

export type ChatSessionItemProps = {
  date: string;
  time: string;
  title: string;
  message?: string;
  isSelected?: boolean;
  onClick?: (event: React.SyntheticEvent) => void;
};

const SELECTED_BAR_WIDTH = '6px';
const MESSAGE_MAX_LENGTH = 60;

function getGraphemes(text: string): string[] {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  return Array.from(segmenter.segment(text), ({ segment }) => segment);
}

function truncateText(text: string, maxLength: number): string {
  if (maxLength <= 0) return '';
  const graphemes = getGraphemes(text);
  if (graphemes.length <= maxLength) return text;
  if (maxLength === 1) return '\u2026';
  return `${graphemes.slice(0, maxLength - 1).join('')}\u2026`;
}

export const ChatSessionItem = React.forwardRef<
  HTMLButtonElement,
  ChatSessionItemProps
>(
  (
    { date, time, title, message, isSelected, onClick },
    ref,
  ): React.ReactElement => {
    const isTabletMobile = useMediaQuery(queryHelpers.down('laptop'));
    const displayMessage = React.useMemo(
      () => (message ? truncateText(message, MESSAGE_MAX_LENGTH) : undefined),
      [message],
    );

    return (
      <Box
        as="button"
        type="button"
        ref={ref}
        aria-current={isSelected && !isTabletMobile ? 'true' : undefined}
        onClick={onClick}
        css={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          textAlign: 'start',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          '&,&:before': {
            transition:
              'background-color 200ms ease-in-out, transform 200ms ease-in-out',
          },
          backgroundColor:
            isSelected && !isTabletMobile
              ? '$surfaceBackgroundSecondary'
              : '$surfaceBackgroundPrimary',
          '&:hover': {
            backgroundColor: '$surfaceBackgroundSecondary',
          },
          '&[aria-current="true"]::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: SELECTED_BAR_WIDTH,
            height: '100%',
            backgroundColor: '$interactiveActionPrimary',
            transform: 'scaleX(1)',
            transformOrigin: 'left',
          },
          ...focusVisibleOverrideInner,
        }}
      >
        <Box
          as="span"
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: '$two',
            flex: 1,
            minWidth: 0,
            padding: '$threeQuarters $oneAndHalf',
          }}
        >
          {/* Timestamp column */}
          <Box as="span" css={{ flexShrink: 0 }}>
            <Box
              as="span"
              css={{
                display: 'block',
                typography: '$subtitleOne',
                color: '$onSurfaceTextPrimary',
              }}
            >
              {date}
            </Box>
            <Box
              as="span"
              css={{
                display: 'block',
                typography: '$caption',
                color: '$onSurfaceTextSubdued',
              }}
            >
              {time}
            </Box>
          </Box>
          {/* Chat details column */}
          <Box as="span" css={{ flex: 1, minWidth: 0 }}>
            <Box
              as="span"
              css={{
                display: 'block',
                typography: '$subtitleOne',
                color: '$onSurfaceTextPrimary',
                maxWidth: '24ch',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Box>
            {displayMessage && (
              <Box
                as="span"
                css={{
                  display: 'block',
                  typography: '$caption',
                  color: '$onSurfaceTextSubdued',
                  overflow: 'hidden',
                  textWrap: 'pretty',
                }}
              >
                {displayMessage}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);

ChatSessionItem.displayName = 'ChatSessionItem';
